import styled, { css } from 'styled-components'
import pluralize from 'pluralize'
import React, { UIEvent, useEffect, useRef, useState, WheelEvent } from 'react'
import { KeywordsTableBody, KeywordsTableHead, useKeywordsTable } from './keywordsTable/KeywordsTable'
import useKeywordsTableData from '../../hooks/useKeywordsTableData'
import Hint from '../../../../shared/hint/Hint'
import { ReactComponent as CheckIcon } from '../../assets/Check.svg'
import HintTitle from './HintTitle'
import { useDatasetsContext } from '../../state/useDatasets'
import { useKeywordsStateContext } from '../../state/useKeywordsState'
import { STICKY_TABLE_HEADER_TOP } from '../../constants'

const ContainerStyled = styled.div`
  position: relative;
  padding-bottom: 16px;
  outline: none;
`

const scrollAreaStyles = css`
  margin-left: 540px;
  border-left: 1px solid ${({ theme }) => theme.colors.grey.primary};
`

const ScrollableContainerStyled = styled.div`
  ${scrollAreaStyles};
  overflow-x: auto;
`

const StickyHeaderStyled = styled.div`
  position: sticky;
  top: ${STICKY_TABLE_HEADER_TOP}px;
  z-index: 2;
  background-color: ${({ theme }) => theme.colors.white};
`

const HeaderScrollContainerStyled = styled.div`
  ${scrollAreaStyles};
  overflow: hidden;
`

const TopScrollContainerStyled = styled.div<{ $isVisible: boolean }>`
  ${scrollAreaStyles};
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
`

const TopScrollContentStyled = styled.div<{ $width: number }>`
  width: ${({ $width }) => `${$width}px`};
  height: 1px;
`

const titleStyles = css`
  font-size: 18px;
  line-height: 22px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
`

const TitleStyled = styled.span`
  ${titleStyles};
`

const InfoWrapperStyled = styled.div`
    display: grid;
    grid-template-columns: 540px 1fr;
`

const TitleWrapperStyled = styled.div`
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const DatasetsCountStyled = styled.span`
  ${titleStyles};

`

const TextWrapperStyled = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 16px;
    border-left: 1px solid ${({ theme }) => theme.colors.grey.primary};
`

const HeaderStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const DescriptionWrapperStyled = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`

const DescriptionStyled = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.grey.dark};
`

const CheckIconStyled = styled(CheckIcon)`
    width: 16px;
    height: 16px;
    display: inline-block;
    vertical-align: middle;
`

const SummaryTextStyled = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.grey.dark};
`

const KeywordsSearchResultsContent = () => {
  const table = useKeywordsTable()
  const datasetsCount = useKeywordsTableData().length
  const { submittedKeywords } = useKeywordsStateContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const topScrollRef = useRef<HTMLDivElement>(null)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const [scrollMetrics, setScrollMetrics] = useState({ width: 0, isVisible: false })

  useEffect(() => {
    const updateScrollMetrics = () => {
      const tableScroll = tableScrollRef.current

      setScrollMetrics({
        width: tableScroll?.scrollWidth ?? 0,
        isVisible: Boolean(tableScroll && tableScroll.scrollWidth > tableScroll.clientWidth)
      })
    }

    updateScrollMetrics()
    window.addEventListener('resize', updateScrollMetrics)

    return () => window.removeEventListener('resize', updateScrollMetrics)
  }, [datasetsCount, submittedKeywords])

  const syncScrollLeft = (scrollLeft: number) => {
    [topScrollRef, headerScrollRef, tableScrollRef].forEach(({ current }) => {
      if (current && current.scrollLeft !== scrollLeft) {
        current.scrollLeft = scrollLeft
      }
    })
  }

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    syncScrollLeft(event.currentTarget.scrollLeft)
  }

  const handleHorizontalWheel = (event: WheelEvent<HTMLDivElement>) => {
    const tableScroll = tableScrollRef.current

    if (!tableScroll || !containerRef.current?.contains(document.activeElement)) {
      return
    }

    const maxScrollLeft = tableScroll.scrollWidth - tableScroll.clientWidth
    const scrollDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
    const nextScrollLeft = Math.max(0, Math.min(tableScroll.scrollLeft + scrollDelta, maxScrollLeft))

    if (nextScrollLeft === tableScroll.scrollLeft) {
      return
    }

    event.preventDefault()
    syncScrollLeft(nextScrollLeft)
  }

  const handleContainerClick = () => {
    containerRef.current?.focus({ preventScroll: true })
  }

  return (
    <ContainerStyled
      ref={containerRef}
      tabIndex={0}
      onClick={handleContainerClick}
    >
      <InfoWrapperStyled>
        <TitleWrapperStyled>
          <DatasetsCountStyled>
            {`${pluralize('Dataset', datasetsCount, true)} found`}
          </DatasetsCountStyled>
          <SummaryTextStyled>
            Click on dataset link to see summary.
          </SummaryTextStyled>
        </TitleWrapperStyled>
        <TextWrapperStyled>
          <HeaderStyled>
            <TitleStyled>
              Availability of variables for the selected search term(s)
            </TitleStyled>
            <Hint withDefaultColor placement="top" maxWidth={280} title={<HintTitle />} />
          </HeaderStyled>
          <DescriptionWrapperStyled>
            <DescriptionStyled>
              Click on search term to compare across datasets.
            </DescriptionStyled>
            <DescriptionStyled>
              Click on
              {' '}
              <CheckIconStyled />
              {' '}
              to see variables associated with each search term for the selected dataset.
            </DescriptionStyled>
          </DescriptionWrapperStyled>
        </TextWrapperStyled>
      </InfoWrapperStyled>
      <StickyHeaderStyled>
        <TopScrollContainerStyled
          ref={topScrollRef}
          $isVisible={scrollMetrics.isVisible}
          onScroll={handleScroll}
          onWheel={handleHorizontalWheel}
        >
          <TopScrollContentStyled $width={scrollMetrics.width} />
        </TopScrollContainerStyled>
        <HeaderScrollContainerStyled ref={headerScrollRef} onWheel={handleHorizontalWheel}>
          <KeywordsTableHead table={table} />
        </HeaderScrollContainerStyled>
      </StickyHeaderStyled>
      <ScrollableContainerStyled ref={tableScrollRef} onScroll={handleScroll} onWheel={handleHorizontalWheel}>
        <KeywordsTableBody table={table} />
      </ScrollableContainerStyled>
    </ContainerStyled>
  )
}

const KeywordsSearchResults = () => {
  const { isLoading } = useDatasetsContext()

  if (isLoading) {
    return <></>
  }
  return <KeywordsSearchResultsContent />
}

export default KeywordsSearchResults
